// LoadingTradeSkeleton — covers the cold-pull dead air with a terminal feel.
// Pure cosmetic. No data. Cycles named steps so 30s reads as "working hard"
// instead of "frozen."

import { useEffect, useState } from "react";
import { Activity, Loader2 } from "lucide-react";

const STEPS = [
  "SCANNING MARKET",
  "READING ORDER BOOK",
  "CHECKING SIGNALS",
  "WEIGHING EDGE",
  "CALLING IT",
];

function slugFromInput(input: string): string {
  if (!input) return "Reading market…";
  try {
    const u = new URL(input);
    const last = u.pathname.split("/").filter(Boolean).pop() ?? "";
    const decoded = decodeURIComponent(last).replace(/[-_]/g, " ").trim();
    return decoded ? decoded.charAt(0).toUpperCase() + decoded.slice(1) : "Reading market…";
  } catch {
    return input.length > 80 ? input.slice(0, 78) + "…" : input;
  }
}

export default function LoadingTradeSkeleton({ input }: { input: string }) {
  const [stepIdx, setStepIdx] = useState(0);
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    const startedAt = Date.now();
    const stepTick = setInterval(() => {
      setStepIdx((i) => Math.min(i + 1, STEPS.length - 1));
    }, 2200);
    const elapsedTick = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startedAt) / 1000));
    }, 250);
    return () => { clearInterval(stepTick); clearInterval(elapsedTick); };
  }, []);

  const slug = slugFromInput(input);

  return (
    <section className="rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/8 to-card overflow-hidden">
      <div className="px-4 pt-4 pb-3 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <span className="inline-flex items-center gap-1 rounded-full border border-primary/40 bg-primary/10 text-primary font-display font-bold uppercase tracking-wider text-[11px] px-2.5 py-1">
            <Activity className="w-3 h-3 animate-pulse" />
            {STEPS[stepIdx]}
          </span>
          <span className="text-[10px] font-body uppercase tracking-wider text-muted-foreground tabular-nums">
            {String(elapsed).padStart(2, "0")}s
          </span>
        </div>

        <h1 className="text-base font-display font-semibold text-foreground/90 leading-snug break-words">
          {slug}
        </h1>

        <div className="space-y-2 pt-1">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center gap-2">
              {i < stepIdx ? (
                <span className="w-3 h-3 rounded-full bg-primary/80 inline-flex items-center justify-center text-[8px] text-primary-foreground">✓</span>
              ) : i === stepIdx ? (
                <Loader2 className="w-3 h-3 animate-spin text-primary" />
              ) : (
                <span className="w-3 h-3 rounded-full border border-border/60" />
              )}
              <span className={`text-[11px] font-body uppercase tracking-wider ${i <= stepIdx ? "text-foreground" : "text-muted-foreground/60"}`}>
                {s}
              </span>
            </div>
          ))}
        </div>

        <p className="text-[10px] font-body text-muted-foreground border-t border-border/40 pt-2">
          Live scan in progress · cold pulls take longer
        </p>
      </div>
    </section>
  );
}
